import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const bubbleContentVariants = cva(
  "relative max-w-[80%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground",
        secondary: "bg-muted text-foreground",
        muted: "bg-muted/60 text-muted-foreground",
        tinted: "bg-primary/10 text-foreground",
        outline: "border bg-background text-foreground",
        ghost: "max-w-none rounded-none px-0 py-0 text-foreground",
        destructive: "bg-destructive text-destructive-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type BubbleVariant = NonNullable<
  VariantProps<typeof bubbleContentVariants>["variant"]
>;

const BubbleContext = React.createContext<{ variant: BubbleVariant }>({
  variant: "default",
});

export interface BubbleProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: BubbleVariant;
  align?: "start" | "end";
}

const Bubble = React.forwardRef<HTMLDivElement, BubbleProps>(
  (
    { variant = "default", align = "start", className, children, ...props },
    ref,
  ) => (
    <BubbleContext.Provider value={{ variant }}>
      <div
        ref={ref}
        data-align={align}
        data-variant={variant}
        className={cn(
          "flex w-full flex-col",
          align === "end" ? "items-end" : "items-start",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </BubbleContext.Provider>
  ),
);
Bubble.displayName = "Bubble";

export interface BubbleContentProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof bubbleContentVariants> {
  asChild?: boolean;
}

const BubbleContent = React.forwardRef<HTMLDivElement, BubbleContentProps>(
  ({ asChild = false, variant, className, ...props }, ref) => {
    const context = React.useContext(BubbleContext);
    const Comp = asChild ? Slot : "div";

    return (
      <Comp
        ref={ref}
        className={cn(
          bubbleContentVariants({ variant: variant ?? context.variant }),
          className,
        )}
        {...props}
      />
    );
  },
);
BubbleContent.displayName = "BubbleContent";

export interface BubbleReactionsProps extends React.HTMLAttributes<HTMLDivElement> {
  side?: "top" | "bottom";
  align?: "start" | "end";
}

const BubbleReactions = React.forwardRef<HTMLDivElement, BubbleReactionsProps>(
  ({ side = "bottom", align = "end", className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex items-center gap-1",
        side === "top" ? "mb-1" : "mt-1",
        align === "end" ? "justify-end" : "justify-start",
        className,
      )}
      {...props}
    />
  ),
);
BubbleReactions.displayName = "BubbleReactions";

const BubbleGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col gap-1", className)} {...props} />
));
BubbleGroup.displayName = "BubbleGroup";

export {
  Bubble,
  BubbleContent,
  BubbleGroup,
  BubbleReactions,
  bubbleContentVariants,
};
